import React from 'react';
import { Link } from 'react-router-dom';
import LegalLayout, { LegalSection } from '../components/LegalLayout';
import { legalInfo, legalDates } from '../lib/legal';
import { businessInfo } from '../mock';

const phoneDigits = String(businessInfo.phone || '').replace(/[^\d]/g, '');

const Terminos = () => (
  <LegalLayout
    title="Términos y Condiciones"
    seoTitle="Términos y Condiciones | Brazolovers"
    seoDescription="Condiciones de compra de Brazolovers en Quito: cómo se confirma un pedido, precios en dólares con IVA, entregas a domicilio y retiro en tienda, cancelaciones, reclamos y alérgenos."
    path="/terminos/"
    updatedAt={legalDates.terminos}
    intro="Estas condiciones explican cómo funcionan los pedidos en brazolovers.com: cuándo queda confirmado un pedido, cómo se cobran los precios y el domicilio, y qué hacer si algo sale mal."
  >
    <LegalSection id="identificacion" heading="1. Quiénes somos">
      <p>
        brazolovers.com es operado por <strong>{legalInfo.razonSocial}</strong>,{' '}
        {legalInfo.tipoPersona} que opera bajo el nombre comercial{' '}
        <strong>{legalInfo.nombreComercial}</strong>, RUC {legalInfo.ruc}, con calificación
        artesanal N.º {legalInfo.calificacionArtesanal} otorgada por la Junta Nacional de Defensa
        del Artesano, y domicilio en {legalInfo.domicilioFiscal}. Puedes contactarnos en{' '}
        <a href={`mailto:${legalInfo.email}`} className="text-caramel font-medium hover:underline">
          {legalInfo.email}
        </a>{' '}
        o por WhatsApp al{' '}
        <a href={`https://wa.me/${phoneDigits}`} target="_blank" rel="noopener noreferrer" className="text-caramel font-medium hover:underline">
          +{phoneDigits}
        </a>.
      </p>
      <p>
        Al usar este sitio y hacer un pedido aceptas estas condiciones y nuestra{' '}
        <Link to="/privacidad/" className="text-caramel font-medium hover:underline">Política de Privacidad</Link>.
      </p>
    </LegalSection>

    <LegalSection id="pedido" heading="2. Cómo se forma el pedido">
      <p>
        Cuando completas el carrito en este sitio, tu pedido se registra y se abre una conversación
        de WhatsApp con nosotros. <strong>Ese registro es una solicitud de compra, no una venta
        cerrada:</strong> el pedido queda en firme cuando Brazolovers lo confirma por WhatsApp,
        indicando disponibilidad, total definitivo y hora de entrega o retiro.
      </p>
      <p>
        Nos reservamos el derecho de no confirmar un pedido por falta de stock, por una dirección
        fuera de nuestra cobertura, por un error evidente de precio o por datos de contacto que no
        podemos verificar. En ese caso te lo comunicaremos y, si ya habías pagado, se te devolverá
        el valor completo.
      </p>
      <p>
        Algunos productos —pasteles personalizados y pedidos por volumen— requieren un mínimo de{' '}
        <strong>24 horas de anticipación</strong> y confirmación expresa de inicio de preparación.
        El sitio te lo advierte al agregarlos al carrito.
      </p>
    </LegalSection>

    <LegalSection id="precios" heading="3. Precios, impuestos y comprobantes">
      <ul className="list-disc list-outside pl-5 space-y-2">
        <li>Todos los precios están expresados en <strong>dólares de los Estados Unidos de América (USD)</strong>, moneda de curso legal en el Ecuador.</li>
        <li>
          Los precios publicados son <strong>precios finales</strong>: lo que ves es lo que pagas
          por el producto. Brazolovers opera bajo <strong>calificación artesanal</strong>{' '}
          (N.º {legalInfo.calificacionArtesanal}, Junta Nacional de Defensa del Artesano) y emite
          sus comprobantes de venta conforme al régimen tributario que le corresponde.
        </li>
        <li>El costo del envío a domicilio <strong>no está incluido</strong> en el precio del producto y se calcula por separado (ver la sección de entregas).</li>
        <li>Emitimos el comprobante de venta correspondiente. Si necesitas factura con tus datos, indícanoslo <strong>antes</strong> de que se emita el comprobante, junto con tu RUC o cédula, nombre, dirección, correo y teléfono.</li>
        <li>Podemos actualizar precios y disponibilidad en cualquier momento; el precio válido es el vigente al momento de confirmar tu pedido.</li>
      </ul>
    </LegalSection>

    <LegalSection id="pago" heading="4. Formas de pago">
      <p>
        Este sitio <strong>no procesa pagos en línea</strong>. La forma de pago (efectivo,
        transferencia u otro medio disponible) se acuerda por WhatsApp al confirmar el pedido. Los
        pedidos que requieren anticipación pueden exigir un abono previo, que se te informará al
        confirmar.
      </p>
    </LegalSection>

    <LegalSection id="entrega" heading="5. Entrega a domicilio y retiro en tienda">
      <ul className="list-disc list-outside pl-5 space-y-2">
        <li>
          <strong>Retiro en tienda:</strong> puedes retirar tu pedido en cualquiera de nuestros
          locales, dentro de su horario de atención. Consulta{' '}
          <Link to="/ubicaciones/" className="text-caramel font-medium hover:underline">ubicaciones y horarios</Link>.
        </li>
        <li>
          <strong>Entrega a domicilio:</strong> disponible dentro de nuestra zona de cobertura en
          Quito. El costo se calcula según la distancia desde nuestro centro de producción y puede
          variar por las condiciones de la zona y el clima. El local confirma la disponibilidad del
          envío antes de despacharlo.
        </li>
        <li>
          Los horarios y ventanas de entrega son <strong>estimados</strong>. Hacemos todo lo posible
          por cumplirlos, pero pueden verse afectados por tráfico, clima o cierres viales.
        </li>
        <li>
          Es tu responsabilidad entregar una dirección correcta y estar disponible en el número que
          nos diste. Si no podemos entregar por datos incorrectos o ausencia del destinatario, el
          costo del envío no es reembolsable y un segundo intento puede tener costo adicional.
        </li>
        <li>
          El <strong>centro de producción no atiende público</strong> ni es punto de retiro.
        </li>
      </ul>
    </LegalSection>

    <LegalSection id="cancelaciones" heading="6. Cancelaciones, cambios y devoluciones">
      <p>
        Nuestros productos son <strong>alimentos frescos y perecibles, elaborados por encargo</strong>.
        Por razones de inocuidad alimentaria no aceptamos devoluciones de productos ya entregados,
        salvo en los casos indicados abajo. Esta limitación es la excepción prevista para bienes
        perecibles y productos elaborados conforme a las especificaciones del consumidor.
      </p>
      <ul className="list-disc list-outside pl-5 space-y-2">
        <li>
          <strong>Cancelación o cambio:</strong> puedes cancelar o modificar tu pedido sin costo
          mientras no haya iniciado la preparación. Para pedidos con 24 horas de anticipación, eso
          significa avisarnos antes de la hora de inicio confirmada.
        </li>
        <li>
          <strong>Producto en mal estado, equivocado o incompleto:</strong> avísanos el{' '}
          <strong>mismo día de la entrega</strong> por WhatsApp, con fotos. Según el caso,
          repondremos el producto o devolveremos su valor.
        </li>
        <li>
          <strong>Pedidos personalizados ya iniciados:</strong> el abono entregado puede no ser
          reembolsable en la parte correspondiente a insumos ya utilizados.
        </li>
      </ul>
      <p>
        Estas condiciones se aplican sin perjuicio de los derechos que te reconoce la{' '}
        <strong>Ley Orgánica de Defensa del Consumidor</strong> del Ecuador.
      </p>
    </LegalSection>

    <LegalSection id="alergenos" heading="7. Alérgenos y conservación">
      <p>
        Nuestros productos se elaboran en una cocina donde se manipulan de forma habitual{' '}
        <strong>gluten (trigo), huevo, leche y derivados lácteos, frutos secos, maní y soya</strong>.
        Aunque cuidamos la preparación, <strong>no podemos garantizar la ausencia de trazas</strong>{' '}
        de estos alérgenos en ningún producto.
      </p>
      <p>
        Si tienes una alergia o intolerancia, consúltanos antes de pedir. Los productos deben
        mantenerse refrigerados y consumirse preferentemente el mismo día de la entrega, salvo
        indicación distinta al momento de la compra.
      </p>
    </LegalSection>

    <LegalSection id="uso" heading="8. Uso del sitio">
      <p>
        Te comprometes a usar este sitio de buena fe y a proporcionar información veraz. No está
        permitido hacer pedidos falsos, intentar acceder a áreas restringidas, extraer contenido de
        forma automatizada ni interferir con el funcionamiento del servicio.
      </p>
      <p>
        Las imágenes son referenciales: por tratarse de productos artesanales, la presentación final
        puede variar ligeramente respecto de las fotografías.
      </p>
    </LegalSection>

    <LegalSection id="propiedad" heading="9. Propiedad intelectual">
      <p>
        <strong>Brazolovers es una marca registrada en el Ecuador</strong> ante el Servicio
        Nacional de Derechos Intelectuales (SENADI). El nombre, el logotipo, el contenido de este
        sitio, los textos, las recetas descritas, las fotografías y los diseños son propiedad de{' '}
        {legalInfo.razonSocial} o se usan con autorización.
      </p>
      <p>
        No pueden reproducirse, imitarse ni usarse con fines comerciales sin permiso escrito. El uso
        no autorizado de la marca da lugar a las acciones previstas en el Código Orgánico de la
        Economía Social de los Conocimientos (Código Ingenios) y demás normativa aplicable.
      </p>
    </LegalSection>

    <LegalSection id="enlaces" heading="10. Enlaces y servicios de terceros">
      <p>
        El sitio enlaza a servicios de terceros (WhatsApp, Google Maps, Uber Eats, redes sociales).
        No controlamos esos servicios ni respondemos por su contenido o sus condiciones, que se
        rigen por sus propios términos.
      </p>
    </LegalSection>

    <LegalSection id="responsabilidad" heading="11. Responsabilidad">
      <p>
        Respondemos por la calidad e inocuidad de los productos que entregamos, en los términos de
        la ley ecuatoriana. No respondemos por daños derivados del uso inadecuado del producto, de
        su conservación fuera de las condiciones indicadas, ni de interrupciones del sitio por
        causas ajenas a nuestro control.
      </p>
    </LegalSection>

    <LegalSection id="reclamos" heading="12. Reclamos y atención al consumidor">
      <p>
        Cualquier reclamo puede presentarse por WhatsApp al{' '}
        <a href={`https://wa.me/${phoneDigits}`} target="_blank" rel="noopener noreferrer" className="text-caramel font-medium hover:underline">
          +{phoneDigits}
        </a>{' '}
        o al correo{' '}
        <a href={`mailto:${legalInfo.email}`} className="text-caramel font-medium hover:underline">
          {legalInfo.email}
        </a>. Te responderemos a la brevedad. Si no llegamos a un acuerdo, conservas tu derecho a
        acudir a la Defensoría del Pueblo y a las demás instancias previstas en la Ley Orgánica de
        Defensa del Consumidor.
      </p>
    </LegalSection>

    <LegalSection id="ley" heading="13. Ley aplicable y jurisdicción">
      <p>
        Estas condiciones se rigen por las leyes de la República del Ecuador. Para cualquier
        controversia, las partes se someten a los jueces competentes de la ciudad de{' '}
        {legalInfo.ciudad}, sin perjuicio de las acciones que el consumidor pueda ejercer ante la
        autoridad de su domicilio.
      </p>
    </LegalSection>

    <LegalSection id="cambios" heading="14. Cambios a estos términos">
      <p>
        Podemos actualizar estas condiciones. La versión aplicable a tu pedido es la publicada en
        esta página al momento de confirmarlo, con la fecha de última actualización indicada al
        inicio.
      </p>
    </LegalSection>
  </LegalLayout>
);

export default Terminos;
